/**
 * MapView — MapLibre GL JS wrapper for the home dashboard (OEM + Pulse).
 *
 * ┌─ FULL ARCHITECTURE: see ./MAPS.md ───────────────────────────────────────┐
 * │ Data pipeline (pmtiles extract command), asset hosting (maps container +  │
 * │ Cloudflare edge Worker), env wiring, the two-layer basemap model (Natural │
 * │ Earth world base + OSM PMTiles US/CA overlay), the layer draw order +     │
 * │ Protomaps schema mapping, and a recreate-from-scratch checklist all live  │
 * │ there.                                                                    │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * Ported from `internal/os/apps/web/src/pages/home/maps/MapView.tsx`.
 *
 * Key adaptations for Gripp/React-Native-Web:
 * - Basemap palette mapped to Crop Circle design tokens (slate-100 for ocean in
 *   light mode, neutral-800 for ocean in dark mode) instead of hardcoded hex.
 * - Container uses inline styles via StyleSheet for RNW compatibility; the
 *   inner <div> for MapLibre still uses className so MapLibre's own CSS rules
 *   (which target .maplibregl-map) continue to work.
 * - `useDarkMode` observes `.dark` on <html> (same as reference).
 * - Web-only — do NOT import this from mobile code.
 */

import { EnvConfig } from '@gripp/shared-logic';
import { tokens } from '@gripp/shared-ui';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { Protocol } from 'pmtiles';
import { ReactNode, useEffect, useRef, useState } from 'react';
import { StyleSheet, View } from 'react-native';

// Register the `pmtiles://` protocol with MapLibre once, at module load, so the
// PMTiles street-level basemap (see pmtilesStyle / EXPO_PUBLIC_MAPS_URL) can be
// fetched via HTTP range requests from a single .pmtiles archive on static
// storage. A module-level guard keeps HMR / repeated imports from registering it
// twice (MapLibre throws on a duplicate protocol). No-op cost when PMTiles is
// unset — the protocol is only ever invoked by a `pmtiles://` source URL.
declare global {
  // eslint-disable-next-line no-var
  var __grippPmtilesProtocolRegistered: boolean | undefined;
}
if (
  !globalThis.__grippPmtilesProtocolRegistered &&
  // Guard for environments where maplibre is mocked/stubbed (jest specs mock the
  // module, so addProtocol is absent) — the protocol is only needed at runtime.
  typeof maplibregl.addProtocol === 'function'
) {
  maplibregl.addProtocol('pmtiles', new Protocol().tile);
  globalThis.__grippPmtilesProtocolRegistered = true;
}

/** One row in a marker's click popup, rendered as "{value} {label}". */
export interface MapMarkerDetail {
  label: string;
  value: string;
}

export interface MapMarker {
  id: string;
  name: string;
  lng: number;
  lat: number;
  /** Dot fill color (hex or CSS color). */
  color: string;
  /** Status bucket — used to aggregate severity counts inside a cluster. */
  bucket?: 'urgent' | 'pending' | 'solved';
  status?: string;
  meta?: string;
  /** Whether to render a pulsing ring around the dot. */
  pulse?: boolean;
  /**
   * Rollup rows shown in a popup when the marker is clicked (e.g. a location's
   * Activities / Assets / Routines counts). Only used in the non-clustered
   * (individual-marker) render path.
   */
  details?: MapMarkerDetail[];
}

/** One point along a track polyline. */
export interface MapTrackPoint {
  lng: number;
  lat: number;
}

/**
 * A single asset track — a chronological polyline plus an endpoint marker at
 * its latest point. Rendered as a GeoJSON LineString `line` layer (same
 * mechanism as the basemap's country borders / US state lines) with one DOM
 * endpoint marker coloured by `color`.
 */
export interface MapTrack {
  id: string;
  /** Endpoint marker label (e.g. the asset name). */
  name: string;
  /** Line + endpoint-dot colour (a status/severity token, safe in CSS/paint). */
  color: string;
  /** Chronologically ordered points; the LAST is the endpoint marker. */
  points: MapTrackPoint[];
}

export interface MapViewProps {
  /** Initial/controlled center [lng, lat]. */
  center: [number, number];
  /** Initial/controlled zoom level. */
  zoom: number;
  markers?: MapMarker[];
  selectedMarkerId?: string | null;
  onMarkerClick?: (marker: MapMarker) => void;
  /**
   * Fired when the pointer enters / leaves an INDIVIDUAL (unclustered) point
   * marker — `markerId` on enter, `null` on leave. NOT fired for cluster bubbles
   * (a cluster aggregates many assets, so a single id is meaningless). The enter
   * is debounced (~150ms) so a fast pan over dots doesn't thrash; the leave
   * clears immediately. Used by the Pulse resting view to highlight the hovered
   * asset's track.
   *
   * Web/hover-only. The native equivalent would be tap-to-select (no hover on
   * touch); that path is out of scope for this web dashboard.
   */
  onMarkerHover?: (markerId: string | null) => void;
  /**
   * Optional asset tracks to draw as polylines (one LineString `line` layer per
   * call, fed from a GeoJSON source) plus one endpoint DOM marker per track at
   * its latest point. Defaults to an empty list — when no tracks are passed the
   * track source/layer is never created, so existing callers (the OEM
   * ActivityMap) are completely unaffected.
   */
  tracks?: MapTrack[];
  /**
   * Id of the track to HIGHLIGHT (the hovered asset). When set, that track's
   * polyline + endpoint marker paint at full strength while the other tracks dim
   * back, so the hovered asset stands out against the always-on field of tracks.
   * null / undefined = no highlight (all tracks at their resting strength). The
   * track `id` is the asset id (see trackToMapTrack), matching onMarkerHover's
   * marker id, so the resting view can wire hover → highlight directly.
   */
  highlightedTrackId?: string | null;
  /**
   * Whether to render an endpoint DOM marker at each track's latest point.
   * Defaults to true. A caller that already plots its own per-asset markers (the
   * Pulse resting view draws the static ASSET points, one labelled dot per asset)
   * passes false to avoid a SECOND dot per asset — the track then renders as a
   * polyline only, and the existing asset marker is the single dot for that asset
   * (it sits at the asset's lastLocation, ~the track's latest point). Hover
   * highlight still works via the asset marker → `highlightedTrackId` line paint.
   */
  showTrackEndpoints?: boolean;
  /** Extra control rendered above the zoom buttons in the controls pill. */
  extraControl?: ReactNode;
  /**
   * When true, the map frames the markers' bounding box (instead of staying at
   * `center`/`zoom`) — so a region-clustered set zooms to that region rather
   * than showing the globe. Falls back to `center`/`zoom` when there are no
   * markers. The frame fires ONCE per `fitKey` (see below), not on every marker
   * change, so a background data refetch can't yank the camera out of a manual
   * zoom.
   */
  fitToMarkers?: boolean;
  /**
   * Identity of the current "framing context" — the deliberate inputs that
   * change WHICH markers show (range / mode / filters / search). When
   * `fitToMarkers` is on, the auto-fit re-frames only when this string changes
   * (and on the first non-empty marker set). It must NOT incorporate the marker
   * DATA itself: the points query refetches on a stale window / window-focus,
   * and re-framing on a refetch that returns changed data would reset a manual
   * zoom — the "streets only show sometimes when I zoom in" bug. Manual
   * re-framing stays available via the "Fit to view" control.
   */
  fitKey?: string;
  /**
   * When true, render markers as a clustered GeoJSON source (circle + count
   * layers) instead of individual DOM markers. Nearby points collapse into a
   * single "(x)" bubble that splits apart as you zoom in. Click a cluster to
   * zoom to its members; click a lone point for a label popup.
   */
  clustered?: boolean;
}

// ---------------------------------------------------------------------------
// Basemap palette — derived from Crop Circle tokens so the map visually
// integrates with the app theme. Comments show the token name.
// ---------------------------------------------------------------------------
const LIGHT_BASEMAP = {
  ocean: tokens.colors.slate[100],     // slate-100 — cool off-white sea
  land: tokens.colors.neutral[100],    // neutral-100 — white/almost-white land
  border: tokens.colors.neutral[300],  // neutral-300 — light border between countries
  road: tokens.colors.slate[200],      // slate-200 — minor roads, faint
  roadMajor: tokens.colors.slate[400], // slate-400 — highways, a touch stronger
  label: tokens.colors.neutral[600],   // neutral-600 — place-name text
  labelHalo: tokens.colors.brand.white,// white halo so text reads over land/road
  waterLabel: tokens.colors.slate[400],// slate-400 — lake names (cooler than land labels)
};

const DARK_BASEMAP = {
  ocean: tokens.colors.neutral[800],   // neutral-800 — near-black sea
  land: tokens.colors.brand.surface,   // brand-surface — slightly lighter land surface
  border: tokens.colors.brand.layer,   // brand-layer — subtle border
  road: tokens.colors.neutral[700],    // neutral-700 — minor roads, faint
  roadMajor: tokens.colors.slate[500], // slate-500 — highways
  label: tokens.colors.slate[200],     // slate-200 — place-name text
  labelHalo: tokens.colors.neutral[800],// dark halo matches the sea
  waterLabel: tokens.colors.slate[400],// slate-400 — lake names
};

// Map assets — glyphs + the Natural Earth world base — are hosted on the maps
// origin (EXPO_PUBLIC_MAPS_URL) alongside the PMTiles archive, NOT bundled in
// the web app. The constants below hold the path RELATIVE to that origin; the
// full URLs are built from `streetUrl` inside pmtilesStyle. So the basemap is a
// single hosted bundle (one place to update, one CDN/cache story) and the web
// export ships zero map data.

// Glyph PBFs (SDF) for the place / water name labels. Open Sans Regular, Latin
// ranges 0-255 + 256-511 (covers US + French-Canadian accents). Provenance:
// Open Sans (Apache-2.0), glyph PBFs from the OpenMapTiles fonts build.
const GLYPHS_PATH = 'fonts/{fontstack}/{range}.pbf';
const LABEL_FONT = 'OpenSansRegular';

// Roads + water labels are a regional concern — hidden on the world overview,
// fading in as you zoom toward a workspace so the global view stays uncluttered.
const ROADS_MAJOR_MINZOOM = 4;
const ROADS_MINOR_MINZOOM = 7;
const LAKE_LABEL_MINZOOM = 5;

// Natural Earth 1:50m admin-0 country polygons (public domain), geometry-only +
// coords rounded to 2dp. The world base under the PMTiles archive: it renders
// land + borders everywhere (the archive only covers the US/CA bbox), so the
// global OEM view shows the whole world and simply gains street detail when
// zoomed into North America. (1:50m over 1:110m gives finer coastlines.)
const WORLD_GEOJSON = 'world-countries-50m.json';

// Lakes (Natural Earth 1:50m), painted in the ocean colour on top of land. The
// admin-0 country polygons don't cut lakes out, so without this layer the Great
// Lakes / Caspian / etc. render as land. Drawn under borders.
const LAKES_GEOJSON = 'lakes-50m.json';

// US state outlines (Natural Earth 1:50m admin-1, US 50 states + DC),
// geometry-only. A subtle line layer that only appears once zoomed into the
// country (see US_STATES_MINZOOM) so the world view stays clean.
const US_STATES_GEOJSON = 'us-states-50m.json';
// State lines start fading in here and reach full strength ~1.5 zoom levels
// later. The whole-world default sits at ~1.5, so they're hidden until you zoom
// into North America.
const US_STATES_MINZOOM = 3;

// ── PMTiles street-level basemap (US + Canada) ────────────────────────────────
// When EXPO_PUBLIC_MAPS_URL is set, the map overlays a single OSM PMTiles vector
// archive (Protomaps Basemap v4 schema) on top of the Natural Earth land/border
// base, fetched per-viewport via HTTP range requests through the `pmtiles://`
// protocol registered above. The NE base stays underneath so areas outside the
// US/CA extract bbox still show land + country borders (graceful fallback); the
// PMTiles layers add finer water, streets, and place labels within it.
// The NE roads / lakes-hi / place-label layers are DROPPED in this mode (the
// PMTiles equivalents replace them) to avoid double-drawing. See spec 0016.
const PMTILES_ARCHIVE = 'us-canada-z12.pmtiles';
// Cache-bust token appended to every maps-asset URL (`?v=`). The maps origin
// serves these assets with no `Cache-Control`, so browsers heuristically cache
// them — and a browser that fetched during the window before the Front Door
// CORS fix (PR #768) holds a CORS-poisoned copy it keeps serving. Bumping this
// changes the URL → a fresh browser + edge cache key → a clean fetch that the
// edge stamps with `Access-Control-Allow-Origin: *`. Bump again only if the edge
// CORS behaviour changes or the assets are re-uploaded. (Web-only file, so no
// mobile OTA tail to consider.)
const MAPS_CACHE_VERSION = '2';
// MapLibre source id for the PMTiles vector tiles, and the Protomaps source-layer
// names we theme (the archive also carries landuse/buildings/pois/landcover/earth,
// left undrawn for the minimal Crop Circle look).
const PM_SRC = 'osm';
// Protomaps `roads.kind` buckets → our existing major/minor road treatment.
const PM_ROAD_MAJOR_KINDS = ['highway', 'major_road'];
const PM_ROAD_MINOR_KINDS = ['medium_road', 'minor_road', 'other'];
// Street-name labels appear only at close zoom (town/street level) — they'd be
// illegibly dense at the fleet-overview scale. Majors label here, minor streets
// one zoom later (see the pm-road-labels filter).
const ROAD_LABEL_MINZOOM = 13;
// ODbL requires an OpenStreetMap credit wherever OSM-derived tiles render.
const OSM_ATTRIBUTION = '© OpenStreetMap contributors';

// GeoJSON source + layer ids for the clustered rendering path.
const CLUSTER_SRC = 'activity-points';
const CLUSTER_LAYER = 'clusters';

// GeoJSON source + layer ids for the asset-track polylines. Drawn as a `line`
// layer (same primitive as the basemap's `borders` / `us-states` lines) above
// the basemap but below the DOM markers, which always render on top.
const TRACKS_SRC = 'asset-tracks';
const TRACKS_LAYER = 'asset-tracks-line';

// Track line emphasis. At rest every track draws at TRACK_LINE_WIDTH /
// TRACK_OPACITY_REST (always-on field). On hover the highlighted track thickens
// + goes fully opaque while the rest dim back to TRACK_OPACITY_DIMMED, so the
// hovered asset's path pops without hiding its neighbours.
const TRACK_LINE_WIDTH = 2;
const TRACK_LINE_WIDTH_HIGHLIGHT = 4;
const TRACK_OPACITY_REST = 0.85;
const TRACK_OPACITY_HIGHLIGHT = 1;
const TRACK_OPACITY_DIMMED = 0.2;

// Debounce (ms) before an individual marker's hover-enter fires onMarkerHover.
// A fast pointer sweep across dots would otherwise trigger a per-dot fetch; the
// delay coalesces to the dot the pointer settles on. Leave fires immediately.
const HOVER_DEBOUNCE_MS = 150;

// Pixel radius within which points collapse into one cluster. This is the
// "area of the dot as it renders" the design intends: dots that would visually
// crowd each other merge, and they separate again as you zoom in (supercluster
// re-clusters per zoom level). Tune to taste — larger = chunkier clusters.
const CLUSTER_RADIUS_PX = 40;

// How much to pull back from the tight marker-bounds fit, in zoom levels. One
// zoom level doubles the scale, so log2(1.1) ≈ 0.14 widens the view ~10% (more
// "outside" visible) while keeping every marker comfortably in frame.
const FIT_ZOOM_OUT = 0.14;

// The bottom card strip floats over the lower ~260px of the map. Frame markers
// into the band ABOVE it by padding the fit asymmetrically (large bottom inset)
// so no pin hides behind the cards. The inset clears the strip height PLUS the
// marker's label pill, which is vertically centered on the dot and so dips
// ~14px below it — at a flush 260 the bottom-most label slips behind the cards.
// Top/sides stay generous so points sit well inside the viewport.
const FIT_PADDING = { top: 72, right: 96, bottom: 300, left: 96 };
// Single/coincident point can't form a bounds box — flyTo + this upward pixel
// offset lifts it into the same clear band above the strip.
const SINGLE_POINT_OFFSET: [number, number] = [0, -110];

// Builds the basemap style: the OSM PMTiles street detail (US + Canada) overlaid
// on a Natural Earth land / lakes / country-border / US-state base. The NE base
// is kept UNDERNEATH so areas outside the PMTiles extract bbox still render land
// + borders — the global OEM view shows the whole world and simply gains street
// detail when zoomed into the US/CA. Crop-Circle-themed via the basemap palette.
// All assets (PMTiles archive, glyphs, NE GeoJSON) are hosted on `streetUrl`
// (EXPO_PUBLIC_MAPS_URL); the map renders only when that origin is configured.
function pmtilesStyle(
  dark: boolean,
  streetUrl: string
): maplibregl.StyleSpecification {
  const c = dark ? DARK_BASEMAP : LIGHT_BASEMAP;
  // All map assets live next to the PMTiles archive on the maps origin. The
  // `?v=` token (MAPS_CACHE_VERSION) cache-busts pre-fix CORS-poisoned copies.
  const asset = (path: string) =>
    `${streetUrl}/${path}${path.includes('?') ? '&' : '?'}v=${MAPS_CACHE_VERSION}`;
  return {
    version: 8 as const,
    projection: { type: 'mercator' as const },
    glyphs: asset(GLYPHS_PATH),
    sources: {
      // Natural Earth base (global land + coarse lakes + US state lines) so the
      // map renders land + borders outside the US/CA PMTiles bbox.
      world: { type: 'geojson' as const, data: asset(WORLD_GEOJSON) },
      lakes: { type: 'geojson' as const, data: asset(LAKES_GEOJSON) },
      'us-states': { type: 'geojson' as const, data: asset(US_STATES_GEOJSON) },
      // OSM PMTiles vector archive (Protomaps v4 schema), range-fetched via the
      // `pmtiles://` protocol. `attribution` feeds MapLibre's metadata; the
      // visible ODbL credit is rendered as a static overlay (see the component).
      [PM_SRC]: {
        type: 'vector' as const,
        url: `pmtiles://${streetUrl}/${PMTILES_ARCHIVE}?v=${MAPS_CACHE_VERSION}`,
        attribution: OSM_ATTRIBUTION,
      },
    },
    // Draw order: ocean → NE land → NE coarse lakes → OSM water
    // (finer lakes/rivers cut the land) → NE borders → NE state lines → OSM
    // roads → OSM place + water labels (top). DOM markers / track lines sit above
    // the whole canvas (added at runtime).
    layers: [
      {
        id: 'ocean-bg',
        type: 'background' as const,
        paint: { 'background-color': c.ocean },
      },
      { id: 'land', type: 'fill' as const, source: 'world', paint: { 'fill-color': c.land } },
      { id: 'lakes', type: 'fill' as const, source: 'lakes', paint: { 'fill-color': c.ocean } },
      // OSM water polygons (lakes, reservoirs, riverbanks, ocean) in the sea
      // colour so they cut cleanly into the land fill.
      {
        id: 'pm-water',
        type: 'fill' as const,
        source: PM_SRC,
        'source-layer': 'water',
        paint: { 'fill-color': c.ocean },
      },
      // OSM rivers / streams / canals as thin lines (these are linework, not
      // polygons, so the fill layer above doesn't render them).
      {
        id: 'pm-water-line',
        type: 'line' as const,
        source: PM_SRC,
        'source-layer': 'water',
        filter: ['in', ['get', 'kind'], ['literal', ['river', 'stream', 'canal']]],
        minzoom: 7,
        layout: { 'line-cap': 'round' as const, 'line-join': 'round' as const },
        paint: {
          'line-color': c.ocean,
          'line-width': ['interpolate', ['linear'], ['zoom'], 7, 0.4, 12, 1.6],
        },
      },
      // NE country borders + zoom-gated US state lines (reused from the NE base).
      {
        id: 'borders',
        type: 'line' as const,
        source: 'world',
        paint: { 'line-color': c.border, 'line-width': 0.5 },
      },
      {
        id: 'us-states',
        type: 'line' as const,
        source: 'us-states',
        minzoom: US_STATES_MINZOOM,
        paint: {
          'line-color': c.border,
          'line-width': 0.5,
          'line-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            US_STATES_MINZOOM,
            0,
            US_STATES_MINZOOM + 1.5,
            0.5,
          ],
        },
      },
      // ── OSM roads (kind-bucketed, zoom-gated) ─────────────────────────────
      // Minor streets (medium/minor/other) — the street-level detail Natural
      // Earth lacks. Faint + late so the regional view stays clean.
      {
        id: 'pm-roads-minor',
        type: 'line' as const,
        source: PM_SRC,
        'source-layer': 'roads',
        minzoom: ROADS_MINOR_MINZOOM,
        filter: ['in', ['get', 'kind'], ['literal', PM_ROAD_MINOR_KINDS]],
        layout: { 'line-cap': 'round' as const, 'line-join': 'round' as const },
        paint: {
          'line-color': c.road,
          'line-width': ['interpolate', ['linear'], ['zoom'], ROADS_MINOR_MINZOOM, 0.3, 12, 1.5, 16, 4],
          'line-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            ROADS_MINOR_MINZOOM,
            0,
            ROADS_MINOR_MINZOOM + 1.5,
            0.7,
          ],
        },
      },
      // Highways + major roads — earlier + stronger.
      {
        id: 'pm-roads-major',
        type: 'line' as const,
        source: PM_SRC,
        'source-layer': 'roads',
        minzoom: ROADS_MAJOR_MINZOOM,
        filter: ['in', ['get', 'kind'], ['literal', PM_ROAD_MAJOR_KINDS]],
        layout: { 'line-cap': 'round' as const, 'line-join': 'round' as const },
        paint: {
          'line-color': c.roadMajor,
          'line-width': ['interpolate', ['linear'], ['zoom'], ROADS_MAJOR_MINZOOM, 0.5, 10, 2, 16, 6],
          'line-opacity': [
            'interpolate',
            ['linear'],
            ['zoom'],
            ROADS_MAJOR_MINZOOM,
            0,
            ROADS_MAJOR_MINZOOM + 1,
            0.85,
          ],
        },
      },
      // ── OSM labels (places + water) ───────────────────────────────────────
      // Localities (cities/towns/villages), revealed by the per-feature
      // `min_zoom` Protomaps assigns (denser places appear only as you zoom in),
      // with collisions resolved in favour of the more important (lower min_zoom)
      // place via symbol-sort-key. Open Sans glyphs (see GLYPHS_PATH).
      {
        id: 'pm-place-labels',
        type: 'symbol' as const,
        source: PM_SRC,
        'source-layer': 'places',
        filter: [
          'all',
          ['==', ['get', 'kind'], 'locality'],
          ['<=', ['coalesce', ['get', 'min_zoom'], 15], ['zoom']],
        ],
        layout: {
          'text-field': ['coalesce', ['get', 'name'], ''],
          'text-font': [LABEL_FONT],
          'text-size': ['interpolate', ['linear'], ['zoom'], 3, 11, 8, 14, 12, 16],
          'text-anchor': 'top' as const,
          'text-offset': [0, 0.4],
          'text-max-width': 8,
          'symbol-sort-key': ['coalesce', ['get', 'min_zoom'], 15],
        },
        paint: {
          'text-color': c.label,
          'text-halo-color': c.labelHalo,
          'text-halo-width': 1.2,
        },
      },
      // Street names along the road centreline. Only at close (town/street) zoom
      // — z12 is the archive's max, so this leans on overzoom; below ROAD_LABEL_
      // MINZOOM the labels would be unreadably dense at fleet-overview scale.
      // Named roads only; majors label ~1 zoom earlier than minor streets.
      {
        id: 'pm-road-labels',
        type: 'symbol' as const,
        source: PM_SRC,
        'source-layer': 'roads',
        minzoom: ROAD_LABEL_MINZOOM,
        filter: [
          'all',
          ['has', 'name'],
          [
            'case',
            ['in', ['get', 'kind'], ['literal', PM_ROAD_MAJOR_KINDS]],
            ['>=', ['zoom'], ROAD_LABEL_MINZOOM],
            ['>=', ['zoom'], ROAD_LABEL_MINZOOM + 1],
          ],
        ],
        layout: {
          'text-field': ['coalesce', ['get', 'name'], ''],
          'text-font': [LABEL_FONT],
          'text-size': ['interpolate', ['linear'], ['zoom'], 13, 10, 16, 12],
          'symbol-placement': 'line' as const,
          'symbol-spacing': 280,
          'text-max-angle': 30,
        },
        paint: {
          'text-color': c.label,
          'text-halo-color': c.labelHalo,
          'text-halo-width': 1.5,
        },
      },
      {
        id: 'pm-water-labels',
        type: 'symbol' as const,
        source: PM_SRC,
        'source-layer': 'water',
        minzoom: LAKE_LABEL_MINZOOM,
        filter: [
          'all',
          ['has', 'name'],
          ['<=', ['coalesce', ['get', 'min_zoom'], 15], ['zoom']],
        ],
        layout: {
          'text-field': ['coalesce', ['get', 'name'], ''],
          'text-font': [LABEL_FONT],
          'text-size': ['interpolate', ['linear'], ['zoom'], 5, 10, 10, 13],
          'text-max-width': 6,
        },
        paint: {
          'text-color': c.waterLabel,
          'text-halo-color': c.labelHalo,
          'text-halo-width': 1,
        },
      },
    ],
  } as maplibregl.StyleSpecification;
}

/**
 * Observes the `.dark` class on `<html>` and returns a reactive boolean,
 * allowing the map to re-initialise when the theme toggle flips.
 */
function useDarkMode(): boolean {
  const [dark, setDark] = useState(() =>
    typeof document !== 'undefined' &&
    document.documentElement.classList.contains('dark')
  );

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const html = document.documentElement;
    const update = () => setDark(html.classList.contains('dark'));
    update();
    const obs = new MutationObserver(update);
    obs.observe(html, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  return dark;
}

// ---------------------------------------------------------------------------
// Marker element builder — shared by the plain DOM-marker path and the
// clustered path's individual (unclustered) points, so a lone point always
// shows its original label pill.
//
// Built with DOM APIs (NOT innerHTML): `name` is the issue title —
// tenant-controlled data — so it MUST be assigned via textContent, never
// interpolated into an HTML string, or a crafted title (e.g. `<img onerror=…>`)
// executes as XSS. `color` is one of our own severity tokens, safe in a style.
// ---------------------------------------------------------------------------
function createMarkerElement(opts: {
  name: string;
  color: string;
  isSelected?: boolean;
  pulse?: boolean;
  /**
   * When true, the label pill captures pointer events and shows a pointer
   * cursor, so clicking the (large, obvious) pill — not just the 14px dot —
   * triggers the marker click. Used for clickable location pins; left off for
   * plain issue labels so they don't swallow map drags.
   */
  interactive?: boolean;
}): HTMLDivElement {
  const {
    name,
    color,
    isSelected = false,
    pulse = false,
    interactive = false,
  } = opts;

  const el = document.createElement('div');
  el.className = 'gripp-map-marker';

  const inner = document.createElement('div');
  inner.className = `gripp-marker-inner${isSelected ? ' is-selected' : ''}`;
  inner.style.cssText =
    'position:relative;width:14px;height:14px;cursor:pointer;';

  if (pulse) {
    const pulseEl = document.createElement('div');
    pulseEl.className = 'gripp-marker-pulse';
    pulseEl.style.cssText =
      'position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:30px;height:30px;border-radius:50%;opacity:0.2;animation:gripp-pulse 2s ease-in-out infinite;pointer-events:none;';
    pulseEl.style.background = color;
    inner.appendChild(pulseEl);
  }

  const dot = document.createElement('div');
  dot.className = 'gripp-marker-dot';
  dot.style.cssText =
    // box-sizing:border-box keeps the dot 14×14 *including* its 2px border, so
    // it stays concentric with the 14×14 inner (and thus the pulse). Without it
    // the default content-box renders the dot 18×18, offsetting it ~2px from the
    // pulse centre. (gripp-os doesn't hit this — Tailwind's base sets border-box
    // globally; these raw createElement nodes don't inherit that reset.)
    'box-sizing:border-box;position:absolute;top:0;left:0;width:14px;height:14px;border-radius:50%;border:2px solid white;box-shadow:0 1px 3px rgba(0,0,0,0.25);transition:transform 180ms cubic-bezier(0.32,0.72,0,1),box-shadow 180ms cubic-bezier(0.32,0.72,0,1);';
  dot.style.background = color;
  inner.appendChild(dot);

  const nameWrap = document.createElement('div');
  nameWrap.className = 'gripp-marker-name';
  nameWrap.style.cssText =
    'position:absolute;left:calc(100% + 6px);top:50%;transform:translateY(-50%);padding:4px 10px;border-radius:999px;background:rgba(2,2,2,0.85);white-space:nowrap;box-shadow:0 2px 8px rgba(0,0,0,0.25);' +
    (interactive ? 'pointer-events:auto;cursor:pointer;' : 'pointer-events:none;');
  const nameSpan = document.createElement('span');
  nameSpan.style.cssText =
    'font-family:Inter,sans-serif;font-weight:600;font-size:11px;line-height:14px;color:white;';
  // Safe: textContent never interprets markup.
  nameSpan.textContent = name;
  nameWrap.appendChild(nameSpan);
  inner.appendChild(nameWrap);

  el.appendChild(inner);
  return el;
}

// ---------------------------------------------------------------------------
// Cluster count label — the number shown inside a cluster bubble. Rendered as a
// DOM marker (NOT a MapLibre symbol layer) so the map needs no glyph server: the
// bubble disc is still the `clusters` circle layer, and this just overlays the
// count, centred, with pointer-events off so a click falls through to the circle
// layer's zoom-to-expand handler. `count` is a numeric aggregate (never tenant
// data), but we still assign via textContent for consistency.
// ---------------------------------------------------------------------------
function createClusterCountElement(count: string): HTMLDivElement {
  const t = tokens.typography.label.mediumEmphasised;
  const el = document.createElement('div');
  el.style.cssText =
    `font-family:${t.fontFamily},sans-serif;font-weight:${t.fontWeight};font-size:${t.fontSize}px;line-height:${t.fontSize}px;color:${tokens.colors.neutral[100]};pointer-events:none;user-select:none;`;
  el.textContent = count;
  return el;
}

// ---------------------------------------------------------------------------
// Popup content builder — the rollup card shown when a location marker is
// clicked. Built with DOM APIs (NOT innerHTML) and textContent, same XSS-safe
// discipline as createMarkerElement: `name` and detail values are data, never
// interpolated into an HTML string.
// ---------------------------------------------------------------------------
function createPopupElement(
  name: string,
  details: MapMarkerDetail[]
): HTMLDivElement {
  const root = document.createElement('div');
  root.style.cssText = 'min-width:120px;padding:2px 2px 0;';

  const title = document.createElement('div');
  title.style.cssText =
    'font-family:Inter,sans-serif;font-weight:600;font-size:13px;line-height:18px;' +
    `color:${tokens.colors.neutral[800]};margin-bottom:6px;`;
  title.textContent = name;
  root.appendChild(title);

  for (const detail of details) {
    const row = document.createElement('div');
    row.style.cssText =
      'font-family:Inter,sans-serif;font-weight:400;font-size:12px;line-height:18px;' +
      `color:${tokens.colors.neutral[600]};`;
    const value = document.createElement('span');
    value.style.cssText = `font-weight:600;color:${tokens.colors.neutral[800]};`;
    value.textContent = detail.value;
    row.appendChild(value);
    // " Activities" — the leading space separates value from label.
    row.appendChild(document.createTextNode(` ${detail.label}`));
    root.appendChild(row);
  }

  return root;
}

// ---------------------------------------------------------------------------
// Build the clustered GeoJSON source data from the marker list. Shared by the
// cluster source's initial `data` and its later `setData` updates so both stay
// in sync.
// ---------------------------------------------------------------------------
function markersToFeatureCollection(
  markers: MapMarker[]
): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: markers.map((m) => ({
      type: 'Feature' as const,
      geometry: { type: 'Point' as const, coordinates: [m.lng, m.lat] },
      properties: {
        id: m.id,
        title: m.name,
        color: m.color,
        bucket: m.bucket ?? 'solved',
      },
    })),
  } as GeoJSON.FeatureCollection;
}

// ---------------------------------------------------------------------------
// Build the GeoJSON LineString FeatureCollection for the asset tracks. Each
// track with 2+ points becomes one LineString feature carrying its `color` so
// the `line` layer can paint per-feature via a `['get','color']` expression.
// Single-point tracks have no line to draw (still get an endpoint marker), so
// they're skipped here.
// ---------------------------------------------------------------------------
function tracksToFeatureCollection(
  tracks: MapTrack[],
  highlightedTrackId?: string | null
): GeoJSON.FeatureCollection {
  return {
    type: 'FeatureCollection',
    features: tracks
      .filter((t) => t.points.length >= 2)
      .map((t) => ({
        type: 'Feature' as const,
        geometry: {
          type: 'LineString' as const,
          coordinates: t.points.map((p) => [p.lng, p.lat]),
        },
        // `highlighted` drives the line's emphasis paint expression; `anyHighlight`
        // lets the non-highlighted features dim ONLY while some track is hovered
        // (so the resting field of tracks stays at full strength until hover).
        properties: {
          id: t.id,
          color: t.color,
          highlighted: !!highlightedTrackId && t.id === highlightedTrackId,
          anyHighlight: !!highlightedTrackId,
        },
      })),
  } as GeoJSON.FeatureCollection;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function MapView({
  center,
  zoom,
  markers = [],
  selectedMarkerId,
  onMarkerClick,
  onMarkerHover,
  extraControl,
  fitToMarkers = false,
  fitKey,
  clustered = false,
  tracks = [],
  highlightedTrackId,
  showTrackEndpoints = true,
}: MapViewProps) {
  // The maps origin (PMTiles archive + glyphs + NE base) — EXPO_PUBLIC_MAPS_URL,
  // read from EnvConfig (mirrors mediaUrl). The basemap renders only when this
  // is configured; an empty value (local dev without the env set) yields no
  // basemap — point it at a maps origin (e.g. https://maps.nonprod.gripp.it).
  const streetUrl = EnvConfig.mapsUrl;
  const containerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<maplibregl.Map | null>(null);
  const markerInstancesRef = useRef<maplibregl.Marker[]>([]);
  // Endpoint DOM markers for the track polylines (one per track, at its latest
  // point). Kept separate from `markerInstancesRef` so the two marker sets are
  // managed by independent effects and never clobber each other.
  const trackEndpointMarkersRef = useRef<maplibregl.Marker[]>([]);
  // Per-point DOM markers in clustered mode, keyed by point id, so an
  // unclustered point shows its original label pill (not a bare dot).
  const singleMarkersRef = useRef<Map<string, maplibregl.Marker>>(new Map());
  // Per-cluster DOM count labels keyed by cluster_id, overlaid on the circle
  // bubbles. Replaces the old symbol/text layer so no glyph server is needed.
  const clusterCountMarkersRef = useRef<Map<string, maplibregl.Marker>>(
    new Map()
  );
  // Single open popup (the clicked marker's rollup card), so a new click
  // replaces the previous popup instead of stacking them.
  const popupRef = useRef<maplibregl.Popup | null>(null);
  // The `fitKey` the camera was last auto-framed for. Gates the auto-fit so it
  // fires once per framing context instead of on every marker refetch. Reset on
  // map (re-)init below so a fresh map re-frames even if the key is unchanged.
  const lastFittedKeyRef = useRef<string | undefined>(undefined);
  const [mapReady, setMapReady] = useState(false);
  const dark = useDarkMode();
  // Latest onMarkerClick, held in a ref so the clustered single-marker click
  // handlers (wired inside the [mapReady, clustered] effect, which does NOT
  // depend on onMarkerClick) always invoke the current callback rather than a
  // stale closure.
  const onMarkerClickRef = useRef(onMarkerClick);
  onMarkerClickRef.current = onMarkerClick;
  // Latest onMarkerHover, held in a ref so the marker mouseenter/mouseleave
  // handlers (wired inside effects that don't depend on onMarkerHover) always
  // call the current callback, not a stale closure.
  const onMarkerHoverRef = useRef(onMarkerHover);
  onMarkerHoverRef.current = onMarkerHover;
  // Pending debounce timer for a marker enter. A fast pointer sweep over several
  // dots would otherwise fire a fetch per dot; we delay the enter ~150ms and a
  // leave (or a new enter) clears the pending timer first. Cleared on unmount
  // too (see the cleanup effect below) — project rule: clean up debounce timers.
  const hoverDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Debounced enter / immediate leave. Shared by every per-point marker so the
  // timer is centrally managed (one pending enter at a time).
  const scheduleHoverEnter = (id: string) => {
    if (hoverDebounceRef.current) clearTimeout(hoverDebounceRef.current);
    hoverDebounceRef.current = setTimeout(() => {
      hoverDebounceRef.current = null;
      onMarkerHoverRef.current?.(id);
    }, HOVER_DEBOUNCE_MS);
  };
  const fireHoverLeave = () => {
    if (hoverDebounceRef.current) {
      clearTimeout(hoverDebounceRef.current);
      hoverDebounceRef.current = null;
    }
    onMarkerHoverRef.current?.(null);
  };

  // Clear any pending hover-enter timer on unmount so it can't fire after the
  // component is gone (project rule: clean up debounce timers in a cleanup).
  useEffect(() => {
    return () => {
      if (hoverDebounceRef.current) {
        clearTimeout(hoverDebounceRef.current);
        hoverDebounceRef.current = null;
      }
    };
  }, []);

  // --------------------------------------------------------------------------
  // Initialise map. Re-runs when the theme flips so the basemap swaps.
  // DOM markers persist across re-inits; track layers live in the style and
  // are automatically re-drawn when `mapReady` flips back to `true`.
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!containerRef.current) return;

    // A fresh map starts at center/zoom, so re-arm the auto-fit (the new map
    // must re-frame even when the framing context is unchanged, e.g. a theme
    // re-init).
    lastFittedKeyRef.current = undefined;

    const map = new maplibregl.Map({
      container: containerRef.current,
      // OSM PMTiles street detail over the Natural Earth world base, all served
      // from the maps origin. The visible ODbL credit is a static overlay (see
      // below), so the built-in "ℹ" attribution control stays off for a clean,
      // edge-to-edge canvas.
      style: pmtilesStyle(dark, streetUrl),
      center,
      zoom,
      attributionControl: false,
    });

    map.on('load', () => {
      // Give the container a chance to be laid out before the first resize so
      // tiles fill the div correctly (same workaround as reference).
      setTimeout(() => map.resize(), 100);
      setMapReady(true);
    });

    mapRef.current = map;

    return () => {
      popupRef.current?.remove();
      popupRef.current = null;
      map.remove();
      mapRef.current = null;
      setMapReady(false);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dark, streetUrl]);

  // --------------------------------------------------------------------------
  // Fly to new center/zoom when props change.
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.flyTo({ center, zoom, duration: 1200 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [center[0], center[1], zoom]);

  // --------------------------------------------------------------------------
  // Render markers. Clears and re-adds on every `markers` change or when
  // `mapReady` flips so markers appear after map re-init.
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!mapRef.current) return;

    // Clear previous marker instances + any open popup. Done BEFORE the
    // clustered bail so a false→true `clustered` toggle tears down the DOM
    // markers this effect rendered (otherwise they'd linger under the
    // GeoJSON layers with stale click handlers).
    markerInstancesRef.current.forEach((m) => m.remove());
    markerInstancesRef.current = [];
    popupRef.current?.remove();
    popupRef.current = null;

    // Clustered mode renders via GeoJSON layers (separate effect), not DOM
    // markers — bail so we don't draw both.
    if (clustered) return;

    markers.forEach((marker) => {
      const hasDetails = !!marker.details && marker.details.length > 0;
      const el = createMarkerElement({
        name: marker.name,
        color: marker.color,
        isSelected: selectedMarkerId === marker.id,
        pulse: marker.pulse,
        interactive: !!onMarkerClick || hasDetails,
      });

      if (onMarkerClick || hasDetails) {
        el.addEventListener('click', (e) => {
          e.stopPropagation();
          onMarkerClick?.(marker);
          const map = mapRef.current;
          if (hasDetails && map) {
            // Replace any open popup with this marker's rollup card.
            popupRef.current?.remove();
            popupRef.current = new maplibregl.Popup({
              offset: 16,
              closeButton: true,
              closeOnClick: true,
            })
              .setLngLat([marker.lng, marker.lat])
              .setDOMContent(
                createPopupElement(marker.name, marker.details ?? [])
              )
              .addTo(map);
          }
        });
      }

      // Hover-to-reveal: these are individual (unclustered) point markers, so
      // each carries one asset id — fire onMarkerHover(id) on enter (debounced)
      // and onMarkerHover(null) on leave. Read via the ref so we don't re-run
      // this effect when the callback identity changes.
      el.addEventListener('mouseenter', () => scheduleHoverEnter(marker.id));
      el.addEventListener('mouseleave', () => fireHoverLeave());

      const map = mapRef.current;
      if (!map) return;
      const instance = new maplibregl.Marker({ element: el, anchor: 'center' })
        .setLngLat([marker.lng, marker.lat])
        .addTo(map);

      markerInstancesRef.current.push(instance);
    });
    // scheduleHoverEnter / fireHoverLeave are stable helpers closing only over
    // refs; intentionally omitted from deps to keep the effect keyed on data.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markers, mapReady, selectedMarkerId, onMarkerClick, clustered]);

  // --------------------------------------------------------------------------
  // Track rendering. Draws each asset track as a GeoJSON LineString in one
  // `line` layer (the same primitive the basemap already uses for country
  // borders / US state lines), plus one endpoint DOM marker per track at its
  // latest point (reusing createMarkerElement so the dot matches the regular
  // markers). Re-runs on `tracks` / `mapReady` change. The line layer is drawn
  // beneath the DOM markers, which always sit on top of the canvas.
  //
  // No-op for callers that pass no tracks: when `tracks` is empty the source /
  // layer is never created and no endpoint markers are added, so existing
  // callers (the OEM ActivityMap, which never passes `tracks`) are unaffected.
  // --------------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !mapReady) return;

    // Always clear any prior endpoint markers first so a `tracks` change (or a
    // post-style-reinit re-run) doesn't leave stale dots behind.
    trackEndpointMarkersRef.current.forEach((m) => m.remove());
    trackEndpointMarkersRef.current = [];

    if (tracks.length === 0) return;

    const data = tracksToFeatureCollection(tracks, highlightedTrackId);

    // Add the line source + layer once; subsequent runs reuse them via setData.
    const existing = map.getSource(TRACKS_SRC) as
      | maplibregl.GeoJSONSource
      | undefined;
    if (existing) {
      existing.setData(data);
    } else {
      map.addSource(TRACKS_SRC, {
        type: 'geojson',
        data,
      } as maplibregl.GeoJSONSourceSpecification);
      map.addLayer({
        id: TRACKS_LAYER,
        type: 'line',
        source: TRACKS_SRC,
        layout: { 'line-cap': 'round', 'line-join': 'round' },
        paint: {
          // Per-feature colour (status token), set in tracksToFeatureCollection.
          'line-color': ['get', 'color'],
          // Highlighted track draws thicker; the rest stay at the resting width.
          'line-width': ['case', ['get', 'highlighted'], TRACK_LINE_WIDTH_HIGHLIGHT, TRACK_LINE_WIDTH],
          // While SOME track is highlighted, dim every non-highlighted track so
          // the hovered one stands out; with nothing hovered every track sits at
          // its full resting opacity (always-on field of tracks).
          'line-opacity': [
            'case',
            ['get', 'highlighted'],
            TRACK_OPACITY_HIGHLIGHT,
            ['case', ['get', 'anyHighlight'], TRACK_OPACITY_DIMMED, TRACK_OPACITY_REST],
          ],
        },
      } as maplibregl.LayerSpecification);
    }
    // Keep the highlighted track's line above its peers so the thicker stroke
    // isn't overdrawn where polylines cross (moveLayer with no `before` raises
    // the layer to the top of the layer stack; the DOM markers still sit above
    // the whole canvas). Guarded — the layer always exists here, but a style
    // reinit race could remove it.
    try {
      if (map.getLayer(TRACKS_LAYER)) map.moveLayer(TRACKS_LAYER);
    } catch {
      /* layer gone mid-reinit — nothing to reorder */
    }

    // One endpoint marker per track at its latest (last) point, coloured to
    // match the track line — reconciles with the resting asset dots. The
    // highlighted asset's endpoint renders selected (scaled + ring) and pulsing
    // so the hovered marker reads as emphasised among the always-on endpoints.
    //
    // Skipped entirely when `showTrackEndpoints` is false: a caller that already
    // plots its own per-asset markers (the Pulse resting view) would otherwise
    // get a duplicate dot per asset. The polyline (above) still renders.
    if (showTrackEndpoints) {
      tracks.forEach((track) => {
        const last = track.points[track.points.length - 1];
        if (!last) return;
        const isHighlighted =
          !!highlightedTrackId && track.id === highlightedTrackId;
        const el = createMarkerElement({
          name: track.name,
          color: track.color,
          isSelected: isHighlighted,
          pulse: isHighlighted,
        });
        const instance = new maplibregl.Marker({ element: el, anchor: 'center' })
          .setLngLat([last.lng, last.lat])
          .addTo(map);
        trackEndpointMarkersRef.current.push(instance);
      });
    }

    // Teardown: remove endpoint markers and the line layer/source. Guarded —
    // the map may already be gone (theme reinit / unmount), where getLayer /
    // removeLayer would throw.
    return () => {
      trackEndpointMarkersRef.current.forEach((m) => m.remove());
      trackEndpointMarkersRef.current = [];
      try {
        if (map.getLayer(TRACKS_LAYER)) map.removeLayer(TRACKS_LAYER);
        if (map.getSource(TRACKS_SRC)) map.removeSource(TRACKS_SRC);
      } catch {
        /* map already torn down (theme reinit / unmount) — nothing to detach */
      }
    };
  }, [tracks, mapReady, highlightedTrackId, showTrackEndpoints]);

  // --------------------------------------------------------------------------
  // Clustered rendering: a GeoJSON source with `cluster: true`. Clusters render
  // as a single constant-colored bubble with a count; nearby points (within
  // CLUSTER_RADIUS_PX at the current zoom) collapse into one bubble and split
  // apart as you zoom in. Individual (unclustered) points are NOT drawn as bare
  // dots — they render as the original labeled DOM markers (dot + name pill),
  // synced from the source on every move/zoom so labels appear as soon as a
  // point stands alone. Clicking a cluster zooms to expand it.
  // --------------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!clustered || !map || !mapReady) return;

    // Fresh setup: clear any stale DOM markers that belonged to a now-destroyed
    // map instance (post-style-reinit) before re-adding the source.
    singleMarkersRef.current.forEach((m) => m.remove());
    singleMarkersRef.current.clear();
    clusterCountMarkersRef.current.forEach((m) => m.remove());
    clusterCountMarkersRef.current.clear();

    // Reconcile the DOM markers against the source's current features. Two kinds:
    //  - Unclustered leaves (no `point_count`) → labeled dot + name pill.
    //  - Clusters (have `point_count`) → a count label overlaid on the circle
    //    bubble (the disc itself is the CLUSTER_LAYER circle; only the number is
    //    a DOM marker, so no glyph server is needed).
    // Add new ones, refresh/reposition existing ones (re-clustering can move a
    // centroid or change a tally), and drop any that have since merged, split,
    // or scrolled out of the loaded tiles.
    const syncDomMarkers = () => {
      if (!map.getSource(CLUSTER_SRC)) return;
      const feats = map.querySourceFeatures(CLUSTER_SRC);
      const seenSingles = new Set<string>();
      const seenClusters = new Set<string>();
      for (const f of feats) {
        const geom = f.geometry as GeoJSON.Point;
        const coords = geom.coordinates as [number, number];

        if (f.properties?.cluster) {
          const cid = String(f.properties?.cluster_id ?? '');
          if (!cid || seenClusters.has(cid)) continue;
          seenClusters.add(cid);
          const count = String(
            f.properties?.point_count_abbreviated ??
              f.properties?.point_count ??
              ''
          );
          const existing = clusterCountMarkersRef.current.get(cid);
          if (existing) {
            existing.setLngLat(coords);
            const node = existing.getElement();
            if (node.textContent !== count) node.textContent = count;
            continue;
          }
          const el = createClusterCountElement(count);
          const instance = new maplibregl.Marker({ element: el, anchor: 'center' })
            .setLngLat(coords)
            .addTo(map);
          clusterCountMarkersRef.current.set(cid, instance);
          continue;
        }

        const id = String(f.properties?.id ?? '');
        if (!id || seenSingles.has(id)) continue;
        seenSingles.add(id);
        if (singleMarkersRef.current.has(id)) continue;
        const [mLng, mLat] = coords;
        const title = String(f.properties?.title ?? '');
        const markerColor = String(
          f.properties?.color ?? tokens.colors.semantic.success
        );
        const el = createMarkerElement({
          name: title,
          color: markerColor,
          // Make the whole label pill clickable (not just the 14px dot) when a
          // click handler is wired, so tapping the asset/activity/product opens
          // its detail page (POC click-to-open, kept atop develop's DOM-marker
          // cluster-count refactor).
          interactive: !!onMarkerClickRef.current,
        });
        if (onMarkerClickRef.current) {
          el.addEventListener('click', (e) => {
            e.stopPropagation();
            onMarkerClickRef.current?.({
              id,
              name: title,
              lng: mLng,
              lat: mLat,
              color: markerColor,
            });
          });
        }
        // Hover-to-reveal track: this is an INDIVIDUAL (unclustered) leaf, so
        // `id` is a single asset id — fire onMarkerHover(id) on enter (debounced)
        // / onMarkerHover(null) on leave. Cluster bubbles (handled above, keyed
        // by cluster_id) deliberately get NO hover handler: a cluster aggregates
        // many assets, so a single id would be meaningless.
        el.addEventListener('mouseenter', () => scheduleHoverEnter(id));
        el.addEventListener('mouseleave', () => fireHoverLeave());
        const instance = new maplibregl.Marker({ element: el, anchor: 'center' })
          .setLngLat(coords)
          .addTo(map);
        singleMarkersRef.current.set(id, instance);
      }
      for (const [id, inst] of singleMarkersRef.current) {
        if (!seenSingles.has(id)) {
          inst.remove();
          singleMarkersRef.current.delete(id);
        }
      }
      for (const [cid, inst] of clusterCountMarkersRef.current) {
        if (!seenClusters.has(cid)) {
          inst.remove();
          clusterCountMarkersRef.current.delete(cid);
        }
      }
    };

    map.addSource(CLUSTER_SRC, {
      type: 'geojson',
      data: markersToFeatureCollection(markers),
      cluster: true,
      clusterRadius: CLUSTER_RADIUS_PX,
      clusterMaxZoom: 16,
    } as maplibregl.GeoJSONSourceSpecification);

    map.addLayer({
      id: CLUSTER_LAYER,
      type: 'circle',
      source: CLUSTER_SRC,
      filter: ['has', 'point_count'],
      paint: {
        // Constant color — clusters are a neutral count bubble, not a status
        // signal. Individual points keep their own severity color.
        'circle-color': tokens.colors.neutral[700],
        // Bubble grows in steps with member count.
        'circle-radius': ['step', ['get', 'point_count'], 15, 10, 20, 50, 26],
        'circle-stroke-width': 2,
        'circle-stroke-color': '#ffffff',
        'circle-opacity': 0.92,
      },
    } as maplibregl.LayerSpecification);

    // Click a cluster → zoom to the level where it expands.
    const onClusterClick = (e: maplibregl.MapLayerMouseEvent) => {
      const feats = map.queryRenderedFeatures(e.point, {
        layers: [CLUSTER_LAYER],
      });
      const clusterId = feats[0]?.properties?.cluster_id as number | undefined;
      if (clusterId == null) return;
      const src = map.getSource(CLUSTER_SRC) as maplibregl.GeoJSONSource;
      src
        .getClusterExpansionZoom(clusterId)
        .then((zoom) => {
          const geom = feats[0].geometry as GeoJSON.Point;
          map.easeTo({
            center: geom.coordinates as [number, number],
            zoom: zoom + 0.3,
            duration: 500,
          });
        })
        .catch(() => {
          /* expansion zoom unavailable — ignore */
        });
    };
    map.on('click', CLUSTER_LAYER, onClusterClick);

    const setPointer = () => {
      map.getCanvas().style.cursor = 'pointer';
    };
    const clearPointer = () => {
      map.getCanvas().style.cursor = '';
    };
    map.on('mouseenter', CLUSTER_LAYER, setPointer);
    map.on('mouseleave', CLUSTER_LAYER, clearPointer);

    // Re-sync DOM markers whenever clustering recomputes (source data settles)
    // or the view changes (pan/zoom changes which points are alone vs. clustered
    // and where each cluster centroid sits).
    const onSourceData = (e: maplibregl.MapSourceDataEvent) => {
      if (e.sourceId === CLUSTER_SRC && map.isSourceLoaded(CLUSTER_SRC)) {
        syncDomMarkers();
      }
    };
    map.on('sourcedata', onSourceData);
    map.on('moveend', syncDomMarkers);
    syncDomMarkers();

    // Teardown — runs when clustering turns OFF (card deselected / filter
    // X-ed out), the map re-inits (theme swap), or the component unmounts.
    // WITHOUT this, the cluster bubbles + their layer/source/handlers and the
    // DOM markers (point labels + cluster counts) linger on top of the resting
    // Location dots. Guarded because the map may already be gone (unmount /
    // theme reinit), in which case getLayer/removeLayer would throw.
    return () => {
      try {
        map.off('click', CLUSTER_LAYER, onClusterClick);
        map.off('mouseenter', CLUSTER_LAYER, setPointer);
        map.off('mouseleave', CLUSTER_LAYER, clearPointer);
        map.off('sourcedata', onSourceData);
        map.off('moveend', syncDomMarkers);
        if (map.getLayer(CLUSTER_LAYER)) map.removeLayer(CLUSTER_LAYER);
        if (map.getSource(CLUSTER_SRC)) map.removeSource(CLUSTER_SRC);
      } catch {
        /* map already torn down (theme reinit / unmount) — nothing to detach */
      }
      // Both refs are stable refs holding mutable Maps of MapLibre markers (not
      // React-rendered nodes), mutated by syncDomMarkers throughout the effect's
      // life — so cleanup must read their LIVE value, not a setup-time snapshot.
      // The exhaustive-deps ref warning is a false positive here.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const single = singleMarkersRef.current;
      single.forEach((m) => m.remove());
      single.clear();
      // eslint-disable-next-line react-hooks/exhaustive-deps
      const clusters = clusterCountMarkersRef.current;
      clusters.forEach((m) => m.remove());
      clusters.clear();
    };
    // `markers` intentionally omitted — data updates live in the effect below so
    // a marker change re-clusters in place instead of rebuilding the source.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady, clustered]);

  // --------------------------------------------------------------------------
  // Clustered rendering — data sync. Pushes the latest markers into the
  // existing cluster source WITHOUT rebuilding it, so search/range changes
  // re-cluster smoothly instead of flashing. The bound sourcedata/moveend
  // handlers re-sync the labeled single-point markers. No-op until the
  // lifecycle effect above has created the source.
  // --------------------------------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    if (!clustered || !map || !mapReady) return;
    const src = map.getSource(CLUSTER_SRC) as
      | maplibregl.GeoJSONSource
      | undefined;
    if (!src) return;
    src.setData(markersToFeatureCollection(markers));
  }, [markers, mapReady, clustered]);

  // --------------------------------------------------------------------------
  // Frame the markers' bounding box. A single (or coincident) point can't form
  // a box, so center+zoom on it instead. `maxZoom` keeps a tightly-clustered
  // set from zooming to street level.
  // --------------------------------------------------------------------------
  const frameMarkers = (duration: number) => {
    const map = mapRef.current;
    if (!map || markers.length === 0) return;
    // The first fit can run before the deferred post-load resize (see the init
    // effect), so the camera would be computed against a stale/smaller canvas
    // and frame the markers too low (bottom pins slip behind the card strip).
    // resize() reads the container's real size synchronously, so cameraForBounds
    // below always uses the true viewport. Subsequent fits are no-ops here.
    map.resize();
    const bounds = new maplibregl.LngLatBounds();
    markers.forEach((m) => bounds.extend([m.lng, m.lat]));
    if (bounds.getNorthEast().distanceTo(bounds.getSouthWest()) < 1) {
      // Single / coincident point: center on it (lifted above the card strip),
      // but don't zoom in hard so it has context around it.
      map.flyTo({
        center: [markers[0].lng, markers[0].lat],
        zoom: 9,
        duration,
        offset: SINGLE_POINT_OFFSET,
      });
      return;
    }
    // Asymmetric padding (large bottom inset) frames the markers into the band
    // above the bottom card strip; a conservative maxZoom keeps a tight cluster
    // from slamming to street level. Compute the fit camera, then ease to a
    // slightly lower zoom (FIT_ZOOM_OUT) so ~10% more of the surrounding map
    // stays visible.
    const camera = map.cameraForBounds(bounds, {
      padding: FIT_PADDING,
      maxZoom: 10,
    });
    if (camera && typeof camera.zoom === 'number') {
      map.easeTo({
        center: camera.center,
        zoom: camera.zoom - FIT_ZOOM_OUT,
        duration,
      });
    } else {
      map.fitBounds(bounds, { padding: FIT_PADDING, duration, maxZoom: 10 });
    }
  };

  // --------------------------------------------------------------------------
  // Auto-fit to markers when enabled. Runs once the map is ready and whenever
  // the marker set changes, so the view frames the data's region instead of
  // sitting on the default world view.
  // --------------------------------------------------------------------------
  useEffect(() => {
    if (!fitToMarkers || !mapReady || markers.length === 0) return;
    // Frame ONCE per framing context. `markers` is in the dep list so the first
    // non-empty set (after data loads) triggers the initial frame, but a later
    // refetch with the SAME `fitKey` is a no-op here — that's what keeps a
    // background data refresh from resetting a manual zoom. A genuine context
    // change (range / mode / filter / search) changes `fitKey` and re-frames;
    // the "Fit to view" control (handleFitView) covers manual re-framing.
    if (lastFittedKeyRef.current === fitKey) return;
    lastFittedKeyRef.current = fitKey;
    frameMarkers(600);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markers, mapReady, fitToMarkers, fitKey]);

  // --------------------------------------------------------------------------
  // Controls — zoom in / zoom out / fit-to-markers
  // --------------------------------------------------------------------------
  const handleZoomIn = () => mapRef.current?.zoomIn();
  const handleZoomOut = () => mapRef.current?.zoomOut();
  const handleFitView = () => frameMarkers(800);

  return (
    <View style={styles.container}>
      {/* Inline <style> for CSS animations + MapLibre overrides. */}
      <style>{`
        @keyframes gripp-pulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.2; }
          50%       { transform: translate(-50%, -50%) scale(2); opacity: 0; }
        }
        .maplibregl-map    { width: 100% !important; height: 100% !important; }
        .maplibregl-canvas { width: 100% !important; height: 100% !important; }
        .gripp-marker-inner:hover .gripp-marker-dot {
          transform: scale(1.3);
        }
        .gripp-marker-inner.is-selected .gripp-marker-dot {
          transform: scale(1.45);
          box-shadow: 0 0 0 3px rgba(255,255,255,0.95), 0 0 0 5px rgba(2,2,2,0.18), 0 2px 6px rgba(0,0,0,0.3) !important;
        }
        .gripp-marker-inner.is-selected:hover .gripp-marker-dot {
          transform: scale(1.55);
        }
        .maplibregl-popup-content {
          border-radius: 10px;
          padding: 12px 14px;
          box-shadow: 0 4px 16px rgba(0,0,0,0.16);
        }
        .maplibregl-popup-close-button {
          font-size: 16px;
          padding: 2px 7px;
          color: ${tokens.colors.neutral[500]};
        }
      `}</style>

      {/* MapLibre container — must be a plain div so MapLibre can write its own
          canvas inside it. Positioned absolute to fill the RNW View. */}
      <div
        ref={containerRef}
        style={{ position: 'absolute', inset: 0 }}
      />

      {/* Controls pill — top-right corner, z-ordered above the map. */}
      <div style={controlsPillStyle}>
        {extraControl && (
          <>
            {extraControl}
            <div style={dividerStyle} />
          </>
        )}

        <button
          onClick={handleZoomIn}
          aria-label="Zoom in"
          style={controlButtonStyle}
          onMouseEnter={(e) =>
            ((e.currentTarget as HTMLButtonElement).style.backgroundColor =
              tokens.colors.neutral[200])
          }
          onMouseLeave={(e) =>
            ((e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent')
          }
        >
          <svg width={14} height={14} viewBox="0 0 16 16" fill="none">
            <path
              d="M0 8H16M8 0V16"
              stroke={tokens.colors.neutral[700]}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div style={dividerStyle} />

        <button
          onClick={handleZoomOut}
          aria-label="Zoom out"
          style={controlButtonStyle}
          onMouseEnter={(e) =>
            ((e.currentTarget as HTMLButtonElement).style.backgroundColor =
              tokens.colors.neutral[200])
          }
          onMouseLeave={(e) =>
            ((e.currentTarget as HTMLButtonElement).style.backgroundColor = 'transparent')
          }
        >
          <svg width={14} height={14} viewBox="0 0 16 16" fill="none">
            <path
              d="M0 8H16"
              stroke={tokens.colors.neutral[700]}
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {markers.length > 0 && (
          <>
            <div style={dividerStyle} />
            <button
              onClick={handleFitView}
              aria-label="Fit to view"
              title="Fit to view"
              style={controlButtonStyle}
              onMouseEnter={(e) =>
                ((e.currentTarget as HTMLButtonElement).style.backgroundColor =
                  tokens.colors.neutral[200])
              }
              onMouseLeave={(e) =>
                ((e.currentTarget as HTMLButtonElement).style.backgroundColor =
                  'transparent')
              }
            >
              <svg width={14} height={14} viewBox="0 0 16 16" fill="none">
                <path
                  d="M6 2H4a2 2 0 0 0-2 2v2"
                  stroke={tokens.colors.neutral[700]}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M10 2h2a2 2 0 0 1 2 2v2"
                  stroke={tokens.colors.neutral[700]}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M2 10v2a2 2 0 0 0 2 2h2"
                  stroke={tokens.colors.neutral[700]}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                <path
                  d="M14 10v2a2 2 0 0 1-2 2h-2"
                  stroke={tokens.colors.neutral[700]}
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
          </>
        )}
      </div>

      {/* ODbL attribution — required wherever the OSM PMTiles basemap renders.
          A static credit (bottom-left, like MapLibre's own control) rather than
          re-enabling the attribution control, so it stays themed + unobtrusive.
          Guarded on the maps origin being configured (no origin ⇒ no basemap). */}
      {streetUrl !== '' && (
        <a
          href="https://www.openstreetmap.org/copyright"
          target="_blank"
          rel="noopener noreferrer"
          style={mapCreditStyle(dark)}
        >
          {OSM_ATTRIBUTION}
        </a>
      )}
    </View>
  );
}

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const styles = StyleSheet.create({
  container: {
    flex: 1,
    overflow: 'hidden',
    minHeight: 200,
  },
});

/** Inline CSS object for the controls pill (plain DOM element). */
const controlsPillStyle: React.CSSProperties = {
  position: 'absolute',
  top: 12,
  right: 12,
  zIndex: 10,
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: `${tokens.colors.brand.white}E6`, // ~90% opacity
  backdropFilter: 'blur(4px)',
  borderRadius: 8,
  boxShadow: '0 1px 4px rgba(0,0,0,0.12)',
  overflow: 'hidden',
};

const controlButtonStyle: React.CSSProperties = {
  width: 32,
  height: 32,
  display: 'grid',
  placeItems: 'center',
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  transition: 'background-color 150ms',
};

const dividerStyle: React.CSSProperties = {
  height: 1,
  backgroundColor: tokens.colors.neutral[300],
};

/** ODbL credit pill, bottom-left — shown only when the PMTiles basemap is on. */
// Minimum-footprint ODbL credit: full "© OpenStreetMap contributors" text (the
// string OSMF's attribution guidelines recommend — kept intact for compliance),
// but stripped of the background pill so it reads as a low-key credit rather than
// a boxed badge. A text halo (mirroring the basemap's own label halos) replaces
// the pill for legibility over arbitrary map content, so the colour must be
// theme-aware — light text + dark halo in dark mode and vice versa.
const mapCreditStyle = (dark: boolean): React.CSSProperties => ({
  position: 'absolute',
  bottom: 4,
  // Bottom-right (MapLibre's own default attribution corner) keeps the credit
  // clear of the marker band + bottom card strip and out of the way at bottom-left.
  right: 6,
  zIndex: 10,
  color: dark
    ? `${tokens.colors.neutral[300]}CC` // ~80% — low-key but legible
    : `${tokens.colors.neutral[600]}CC`,
  textShadow: dark
    ? `0 0 2px ${tokens.colors.neutral[800]}, 0 0 3px ${tokens.colors.neutral[800]}`
    : `0 0 2px ${tokens.colors.brand.white}, 0 0 3px ${tokens.colors.brand.white}`,
  fontFamily: 'Inter,sans-serif',
  fontSize: 9,
  lineHeight: '12px',
  textDecoration: 'none',
});
